import { AiSendIcon, AiStopIcon, Button, IconButton, Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { InputBase, InputBaseProps } from '@mui/material';
import { Close, KeyboardReturn } from 'mdi-material-ui';
import { useEffect, useRef } from 'react';
import { useQueryAssistant } from './query-assistant-context';
import { useSuggestQueryFn } from './query-assistant-hooks';
import { getCommandSymbol, ShortcutPill } from './ShortcutPill';
import { ThinkingState } from './ThinkingState';
import { PROMQL_THINKING_MESSAGES } from './thinking-messages';

export function QueryAssistant() {
  const suggestQuery = useSuggestQueryFn();

  const {
    closeAssistant,
    query,
    onChange,
    inputValue,
    setInputValue,
    hasInputValue,
    isLoading,
    setIsLoading,
    previousPrompt,
    setPreviousPrompt,
    history,
    setHistory,
    errorMessage,
    setErrorMessage,
    setSuggestion,
    handleCancel,
    cancelAndSetAbortController,
  } = useQueryAssistant();

  const handleSubmit = async () => {
    if (!suggestQuery || !hasInputValue || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    const abortController = new AbortController();
    cancelAndSetAbortController(abortController);

    const input = inputValue.trim();
    const existingQuery = query;

    try {
      const suggestion = await suggestQuery({ input, existingQuery, history, abortSignal: abortController.signal });

      setSuggestion({ ...suggestion, metadata: { ...suggestion.metadata, input, existingQuery } });
      onChange(suggestion.query);
      setHistory((history) => [
        ...history,
        { type: 'request' as const, content: input, existingQuery },
        { type: 'response' as const, content: suggestion.query, status: 'success' as const },
      ]);
    } catch (error) {
      if (!abortController.signal.aborted) {
        let errMessage: string | undefined;
        if (error instanceof Error && error.name === 'RequestNotSupportedError') {
          errMessage = error.message;
        } else if (error instanceof Error && error.name === 'TimeoutError') {
          errMessage = 'Query generation timed out. Please try again.';
        } else {
          errMessage = 'Failed to generate a query. Please try again.';
        }

        setErrorMessage(errMessage);
        setHistory((history) => [
          ...history,
          { type: 'request' as const, content: input, existingQuery },
          { type: 'response' as const, content: errMessage, status: 'error' as const },
        ]);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        setPreviousPrompt(input);
        setInputValue('');
      }
    }
  };

  return (
    <Stack gap={0.5}>
      <Stack
        gap={0.5}
        paddingX={1.5}
        paddingY={1}
        maxHeight={200}
        overflow="auto"
        borderRadius={1}
        bgcolor="background.paper"
      >
        {previousPrompt && !isLoading ? (
          <>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Stack flexGrow={1}>
                <PreviousInput value={previousPrompt} />
              </Stack>
              <Tooltip Content="Close">
                <IconButton aria-label="Close" size="small" onClick={closeAssistant}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <ActiveInput onSubmit={handleSubmit} />
          </>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Stack flexGrow={1}>
              <ActiveInput onSubmit={handleSubmit} />
            </Stack>
            <Tooltip Content="Close">
              <IconButton aria-label="Close" size="small" onClick={closeAssistant}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        {isLoading && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <ThinkingState messages={PROMQL_THINKING_MESSAGES} maxDuration={4} />
            <Tooltip Content="Stop">
              <IconButton aria-label="Stop query generation" size="small" onClick={handleCancel}>
                <AiStopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      {errorMessage && (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
}

function preventDefaultAndStopPropagation(e: React.KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
}

function ActiveInput({ onSubmit }: { onSubmit: () => Promise<void> }) {
  const {
    query,
    inputValue,
    setInputValue,
    hasInputValue,
    isLoading,
    suggestion,
    handleAcceptAndRun,
    handleAccept,
    handleReject,
    closeAssistant,
  } = useQueryAssistant();

  const inputRef = useRef<HTMLInputElement>(null);

  // Re-focus the input when it becomes editable again
  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Input
        inputRef={inputRef}
        disabled={isLoading}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={async (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            // Cmd+Enter: Accept + run query
            preventDefaultAndStopPropagation(e);
            handleAcceptAndRun();
          } else if (e.key === 'Enter') {
            // Enter: Generate query
            preventDefaultAndStopPropagation(e);
            await onSubmit();
          } else if (e.key === 'Tab') {
            // Tab: Accept query
            if (suggestion) {
              preventDefaultAndStopPropagation(e);
              handleAccept();
            }
          } else if (e.key === 'Escape') {
            // Escape: Reject query or close
            if (suggestion) {
              preventDefaultAndStopPropagation(e);
              handleReject();
            } else {
              preventDefaultAndStopPropagation(e);
              closeAssistant();
            }
          }
        }}
        placeholder={query ? 'Refine your query' : 'Describe your query'}
      />
      {!isLoading && (
        <Tooltip Content="Generate">
          <span>
            <IconButton aria-label="Generate query" size="small" onClick={onSubmit} disabled={!hasInputValue}>
              <AiSendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

function PreviousInput({ value }: { value?: string }) {
  return <Input value={value} disabled />;
}

function Input(props: InputBaseProps) {
  return (
    <InputBase
      {...props}
      fullWidth
      sx={{
        // TODO: Consider updating Query Generation UI for PANW rebrand
        backgroundColor: 'inherit !important',
        '&.MuiInputBase-root': { py: '2px !important' },
        ...props.sx,
      }}
    />
  );
}

export function QueryAssistantButton() {
  const { openAssistant } = useQueryAssistant();

  return (
    <Button
      size="small"
      variant="secondary"
      data-testid="edit-with-ai-button"
      onClick={() => {
        openAssistant();
      }}
      sx={(t) => {
        const legacyBackground = t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.background.paper;
        const legacyHoverBackground = t.palette.mode === 'light' ? t.palette.grey[100] : t.palette.action.hover;
        return {
          flexShrink: 0,
          px: 1,
          whiteSpace: 'nowrap',
          color: 'text.secondary',
          bgcolor: t.featureFlags.panwRebrandStubYear ? undefined : legacyBackground,
          border: t.featureFlags.panwRebrandStubYear ? undefined : `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          '&:hover': {
            color: 'text.primary',
            bgcolor: t.featureFlags.panwRebrandStubYear ? undefined : legacyHoverBackground,
            border: t.featureFlags.panwRebrandStubYear ? undefined : `1px solid ${t.palette.divider}`,
          },
        };
      }}
    >
      Edit with AI
    </Button>
  );
}

type QueryAssistantAcceptAndRejectProps = {
  onAccept?: () => void;
  onReject?: () => void;
};

export function QueryAssistantAcceptAndReject({ onAccept, onReject }: QueryAssistantAcceptAndRejectProps) {
  const { handleAcceptAndRun, handleAccept, handleReject } = useQueryAssistant();

  return (
    <Stack direction="row" gap={1}>
      <Button
        size="small"
        variant="primary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleAcceptAndRun();
          onAccept?.();
        }}
      >
        Accept and run
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {getCommandSymbol()} <KeyboardReturn />
          </Typography>
        </ShortcutPill>
      </Button>
      <Button
        size="small"
        variant="secondary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleAccept();
          onAccept?.();
        }}
      >
        Accept
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Tab
          </Typography>
        </ShortcutPill>
      </Button>
      <Button
        size="small"
        variant="secondary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleReject();
          onReject?.();
        }}
      >
        Reject
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Esc
          </Typography>
        </ShortcutPill>
      </Button>
    </Stack>
  );
}
